import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useUserProfile } from '../../context/UserProfileContext';
import { getConversation, patchConversation, uploadThreadFile, createThreadForConversation, listThreadFiles } from '../../infrastructure/apiClient';
import { useClients } from '../../context/ClientContext';
import { Conversation, createConversation } from '../../domain/models';
import { applyStreamingUpdate, prepareUserSend, preparePromptSend } from '../../utils/chatHelpers';
import { SignalRService } from '../../apis/SignalRService';
import appConfig from '../../AppConfig';
import ChatWelcomeScreen from './ChatWelcomeScreen';
import ChatMessagesList from './ChatMessagesList';
import ChatFloatingInput from './ChatFloatingInput';
import styles from './Chat.module.scss';


// Removed local in-memory repository in favor of backend persistence APIs

// A brand-new thread can take a moment to become visible on the backend's
// upload path right after creation. The upload endpoint then fails to look
// up the thread record and throws (404 if handled, 500 if not) even though
// the thread was just created successfully. Retry once after a short delay.
const uploadFileWithRetry = async (tenantId: string, threadId: string, file: File) => {
  try {
    return await uploadThreadFile(tenantId, threadId, file);
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === 404) {
      await new Promise(resolve => setTimeout(resolve, 800));
      return uploadThreadFile(tenantId, threadId, file);
    }
    throw err;
  }
};

interface Props {
  conversationId: string | null;
  onCreated?: (id: string) => void;
  onHasMessagesChange?: (hasMessages: boolean) => void;
}

const ChatPane = ({ conversationId, onCreated, onHasMessagesChange }: Props) => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [retryPrompt, setRetryPrompt] = useState<string | null>(null);
  const [aiFailed, setAiFailed] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [threadFileCount, setThreadFileCount] = useState(0);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const { activeClient } = useClients();
  const { profile } = useUserProfile();
  const serviceRef = useRef<SignalRService | null>(null);

  const userName = profile?.name || undefined;

  useEffect(() => {
    const uniqueKey = window.crypto.randomUUID();
    console.log('ChatPane mounting, connecting SignalR with key:', uniqueKey);
    if (!serviceRef.current) {
      serviceRef.current = new SignalRService();
    }
    const signalRService = serviceRef.current;
    signalRService.connect(appConfig.signalR.negotiateUrl, appConfig.signalR.hubName, {
      onMessage: (message) => {
        // Accept statuses beyond MessageUpdate to manage lifecycle
        if (!['MessageUpdate','Started','Completed','Failed'].includes(message.Status)) return;
        console.log(uniqueKey + ' Message received from SignalR:', message);
        setConversation(conv => {
          if (!conv) return conv;
          if (message.ThreadId !== conv.id) return conv;
          const { conversation: updated, stopSending, aiFailed: failed } = applyStreamingUpdate(conv, message.Status, message.Content);
          if (stopSending) setSending(false);
          if (failed) setAiFailed(true);
          return updated;
        });
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      },
      onTitleChange: (update) => {
        if (update.ThreadId === conversation?.id) {
          setConversation((conv) => conv ? { ...conv, title: update.NewTitle } : conv);
        }
      },
      onConnectionStatusChange: (status) => {
        console.log('SignalR connection status:', status);
      },
      onError: (error) => {
        console.error('SignalR error:', error);
      }
  });

    return () => {
      console.log('ChatPane unmounted, SignalR disconnected: ', uniqueKey);
      signalRService.disconnect();
    };
  }, []);

  useEffect(() => {
    if (conversationId === null) {
      setConversation(null);
      setInput('');
      setSending(false);
      setRetryPrompt(null);
      setAiFailed(false);
      setAttachedFiles([]);
      setThreadFileCount(0);
    }
  }, [conversationId]);

  useEffect(() => {
    setConversation(null);
    setInput('');
    setSending(false);
    setRetryPrompt(null);
    setAiFailed(false);
    setAttachedFiles([]);
    setThreadFileCount(0);
    onHasMessagesChange?.(false);
  }, [activeClient?.id, onHasMessagesChange]);

  // Loadconversationwhenidchanges 
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let mounted = true;
    (async () => {
      if (activeClient && conversationId) {
        try {
          const dto = await getConversation(activeClient.id, conversationId, signal);
          if (!mounted) return;
          
          // Only update if we don't have a conversation already, or if the fetched one has messages
          // This prevents overwriting a newly created conversation with an empty one from the backend
          setConversation(prev => {
            // If we already have this conversation with messages, don't overwrite with potentially stale data
            if (prev && prev.id === dto.id && prev.messages.length > 0 && dto.messages.length === 0) {
              console.log('Skipping conversation update - local state has messages, fetched data is empty');
              return prev;
            }
            
            return {
              id: dto.id,
              title: dto.title,
              createdAt: new Date(dto.createdAt),
              updatedAt: new Date(dto.updatedAt),
              messages: dto.messages.map(m => ({ ...m, createdAt: new Date(m.createdAt) })) as any,
            };
          });

          listThreadFiles(activeClient.id, conversationId, signal)
            .then(uploads => { if (mounted) setThreadFileCount(uploads.length); })
            .catch(e => console.warn('Failed to fetch thread uploads', e));
        } catch (e) {
          if (axios.isAxiosError(e) && e.response?.status === 404) {
            // Thread exists on the frontend but not in the backend (e.g. pre-migration
            // Foundry-format threads). Clear it so the user lands on the welcome screen
            // rather than hanging indefinitely on a loading state.
            console.warn('Thread not found in backend, clearing selection', conversationId);
            onCreated?.('');
            message.warning('This conversation is no longer available. Please start a new one.');
          } else {
            console.warn('Failed to load conversation', e);
          }
        }
      }
    })();
    return () => { 
      controller.abort();
      mounted = false; 
    };
  }, [conversationId, activeClient?.id]);

  const handleFilesAttach = (files: File[]) => {
    setAttachedFiles(prev => [...prev, ...files]);
  };

  const handleFileRemove = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if ((!input.trim() && attachedFiles.length === 0) || sending) return;
    if (!activeClient) {
      message.error('No active client selected.');
      return;
    }
    try {
      setSending(true);

      let workingConversation = conversation;
      let isNewThread = false;

      // Uploads are thread-scoped, so a thread must exist before attaching files.
      if (attachedFiles.length > 0 && !workingConversation) {
        const threadId = await createThreadForConversation(activeClient.id);
        workingConversation = createConversation({ id: threadId });
        onCreated?.(threadId);
        isNewThread = true;
      }

      // Give the backend a moment to make the newly created thread visible
      // to the upload endpoint before attempting the first upload against it.
      if (isNewThread) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      let uploadedFileNames: string[] = [];
      if (attachedFiles.length > 0 && workingConversation) {
        const threadId = workingConversation.id;
        const results = await Promise.allSettled(
          attachedFiles.map(file => uploadFileWithRetry(activeClient.id, threadId, file))
        );
        const failedNames = results
          .map((r, i) => (r.status === 'rejected' ? attachedFiles[i].name : null))
          .filter(Boolean) as string[];
        uploadedFileNames = attachedFiles
          .filter((_, i) => results[i].status === 'fulfilled')
          .map(f => f.name);
        const succeeded = results.filter(r => r.status === 'fulfilled').length;
        if (failedNames.length > 0) {
          const suffix = input.trim() ? '. Your message will still be sent.' : '';
          message.warning(`Could not upload: ${failedNames.join(', ')}${suffix}`);
        }
        // Nothing to send: no text and every upload failed
        if (!input.trim() && uploadedFileNames.length === 0) {
          setSending(false);
          return;
        }
        setThreadFileCount(prev => prev + succeeded);
        setAttachedFiles([]);
      }
      const userText = input.trim() || `Uploaded file(s): ${uploadedFileNames.join(', ')}`;
      const result = await prepareUserSend(workingConversation, activeClient.id, userText, onCreated, uploadedFileNames.length > 0 ? uploadedFileNames : undefined);
      setConversation({ ...result.conversation });
      setInput('');
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
      setAiFailed(false);
      setRetryPrompt(null);
    } catch (err) {
      console.error('Chat send failed', err);
      message.error('AI response failed. Please retry.');
      if (!retryPrompt) setRetryPrompt(input.trim() || retryPrompt);
      setAiFailed(true);
      try {
        if (conversation) {
          const current = (conversation as any).retryAttempts || 0;
          await patchConversation(conversation.id, { retryAttempts: current + 1 });
          (conversation as any).retryAttempts = current + 1;
          window.dispatchEvent(new CustomEvent('conversation:updated', { detail: { id: conversation.id, field: 'retryAttempts' } }));
        }
      } catch (e) { /* silent */ }
      setSending(false);
    } finally {
      // keep sending=true until first AI chunk arrives
    }
  };

  const handleRetry = async () => {
    if (!retryPrompt || sending) return;
    if (!activeClient) {
      message.error('No active client selected.');
      return;
    }
    if (!conversation) return; // retry only valid with existing conversation
    try {
      setSending(true);
      // Reuse generic prompt send helper without appending duplicate user message locally
      const result = await preparePromptSend(conversation, activeClient.id, retryPrompt, undefined, { appendUserMessage: false });
      setConversation({ ...result.conversation });
      setAiFailed(false);
      // Keep retryPrompt in case of another failure until success streaming begins; clear now for simplicity
      setRetryPrompt(null);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
    } catch (e) {
      console.error('Retry failed', e);
      message.error('Retry failed.');
      setSending(false);
    } finally {
      // sending flag cleared by streaming chunks on success path
    }
  };

  const hasMessages = (conversation?.messages.length || 0) > 0;

  useEffect(() => {
    onHasMessagesChange?.(hasMessages);
  }, [hasMessages, onHasMessagesChange]);

  return (
    <div className={styles.chatPaneRoot}>
      <div className={hasMessages ? styles.scrollableAreaWithMessages : styles.scrollableAreaWelcome}>
        <div className={styles.contentContainer}>
          {!hasMessages ? (
            <ChatWelcomeScreen
              value={input}
              onChange={setInput}
              onSend={handleSend}
              sending={sending}
              userName={userName}
              attachedFiles={attachedFiles}
              onFilesAttach={handleFilesAttach}
              onFileRemove={handleFileRemove}
              threadFileCount={threadFileCount}
            />
          ) : (
            <ChatMessagesList
              messages={conversation?.messages || []}
              sending={sending}
              aiFailed={aiFailed}
              retryPrompt={retryPrompt}
              onRetry={handleRetry}
              bottomRef={bottomRef}
              tenantId={activeClient?.id}
              threadId={conversation?.id}
            />
          )}
        </div>
      </div>

      {hasMessages && (
        <ChatFloatingInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          sending={sending}
          attachedFiles={attachedFiles}
          onFilesAttach={handleFilesAttach}
          onFileRemove={handleFileRemove}
          threadFileCount={threadFileCount}
        />
      )}
    </div>
  );
};

export default ChatPane;
